const getVal = (obj, keys) => keys.split('.').reduce((o, k) => (o || {})[k], obj);

const isValNumber = (obj, keys) => !isNaN(parseFloat(getVal(obj, keys)));

const roundFloat = (val, exp) => {
	const leadingZero = parseFloat(`1e${exp}`);

	return Math.round(val * leadingZero) / leadingZero;
};

const getRandomID = () => parseInt(Math.random() * 1e8);

const formataCEP = strCEP => strCEP.replace(/^(\d{5})(\d{3})/, '$1-$2');

const formataTelefone = strTelefone =>
	strTelefone.length === 10
		? strTelefone.replace(/^(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
		: strTelefone.length === 11
		? strTelefone.replace(/^(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
		: strTelefone.replace(/^(\d*)/, '+$1');

const formataCPFCNPJ = str => {
	if (!str) return str;
	const limpo = str.replace(/[.\-/]/g, '');
	if (limpo.length === 11) {
		return `${limpo.slice(0, 3)}.${limpo.slice(3, 6)}.${limpo.slice(6, 9)}-${limpo.slice(9)}`;
	}
	if (limpo.length === 14) {
		return `${limpo.slice(0, 2)}.${limpo.slice(2, 5)}.${limpo.slice(5, 8)}/${limpo.slice(8, 12)}-${limpo.slice(12)}`;
	}
	return str;
};

const formataCurrency = value =>
	!isNaN(parseFloat(value)) ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'R$ 0,00';

const formataData = value => !isNaN(parseFloat(value)) ? new Intl.DateTimeFormat('en-GB').format(new Date(value)) : '-';

const formataDataHora = value => {
	const options = {
		year: 'numeric',
		month: 'numeric',
		day: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric',
		hour12: false,
	};

	return new Intl.DateTimeFormat('pt-BR', options).format(new Date(value));
};

const setAllNull = obj =>
	Object.keys(obj).reduce((accObj, parseObj) => {
		if (obj[parseObj] instanceof Object) {
			accObj[parseObj] = setAllTrue(obj[parseObj]);
		} else {
			accObj[parseObj] = null;
		}

		return accObj;
	}, {});

const setAllTrue = obj =>
	Object.keys(obj).reduce((accObj, parseObj) => {
		if (obj[parseObj] instanceof Object) {
			accObj[parseObj] = setAllTrue(obj[parseObj]);
		} else {
			accObj[parseObj] = true;
		}

		return accObj;
	}, {});

// Validação de CNPJ com suporte ao formato alfanumérico
// Formato: [A-Z0-9]{12}[0-9]{2} — 12 posições alfanuméricas + 2 dígitos verificadores
// Cálculo do DV: valor ASCII do caractere - 48 (0=0, 1=1, ..., A=17, B=18, C=19, ...)
const validarCNPJ = cnpj => {
	if (!cnpj) return true;

	// Remove caracteres de formatação (pontos, barra, hífen)
	cnpj = cnpj.replace(/[.\-/]/g, '').toUpperCase();

	if (cnpj.length !== 14) return false;

	// Valida formato: 12 posições alfanuméricas (A-Z, 0-9) + 2 dígitos numéricos
	if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) return false;

	// Elimina CNPJs com todos os caracteres iguais (apenas numéricos)
	if (/^(\d)\1{13}$/.test(cnpj)) return false;

	// Converte caractere para valor numérico usando tabela ASCII - 48
	// Números: '0'(48)-'9'(57) => 0-9
	// Letras: 'A'(65)-'Z'(90) => 17-42
	const charToValue = ch => ch.charCodeAt(0) - 48;

	// Calcula primeiro dígito verificador
	const tamanho1 = 12;
	let soma = 0;
	let pos = tamanho1 - 7; // pos inicia em 5
	for (let i = tamanho1; i >= 1; i--) {
		soma += charToValue(cnpj.charAt(tamanho1 - i)) * pos--;
		if (pos < 2) pos = 9;
	}
	let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
	if (resultado !== parseInt(cnpj.charAt(12))) return false;

	// Calcula segundo dígito verificador
	const tamanho2 = 13;
	soma = 0;
	pos = tamanho2 - 7; // pos inicia em 6
	for (let i = tamanho2; i >= 1; i--) {
		soma += charToValue(cnpj.charAt(tamanho2 - i)) * pos--;
		if (pos < 2) pos = 9;
	}
	resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
	if (resultado !== parseInt(cnpj.charAt(13))) return false;

	return true;
};

// https://www.geradorcpf.com/javascript-validar-cpf.htm
const validarCPF = cpf => {
	if (!cpf) return true;
	cpf = cpf.replace(/[^\d]+/g, '');
	// Elimina CPFs invalidos conhecidos
	if (
		cpf.length !== 11 ||
		cpf === '00000000000' ||
		cpf === '11111111111' ||
		cpf === '22222222222' ||
		cpf === '33333333333' ||
		cpf === '44444444444' ||
		cpf === '55555555555' ||
		cpf === '66666666666' ||
		cpf === '77777777777' ||
		cpf === '88888888888' ||
		cpf === '99999999999'
	)
		return false;
	// Valida 1o digito
	let add = 0;
	for (let i = 0; i < 9; i++) add += parseInt(cpf.charAt(i)) * (10 - i);
	let rev = 11 - (add % 11);
	if (rev === 10 || rev === 11) rev = 0;
	if (rev !== parseInt(cpf.charAt(9))) return false;
	// Valida 2o digito
	add = 0;
	for (let i = 0; i < 10; i++) add += parseInt(cpf.charAt(i)) * (11 - i);
	rev = 11 - (add % 11);
	if (rev === 10 || rev === 11) rev = 0;
	if (rev !== parseInt(cpf.charAt(10))) return false;

	return true;
};

// http://mtfsantos.blogspot.com/2011/09/verificando-inscricao-estadual-em.html
const validarIE = (ie, estado = 'PR') => {
	ie = ie.replace(/[^\d]+/g, '');
	if (ie != '') {
		var dig8 = '/BA*/RJ*';
		var dig9 = '/AL*/AP*/AM*/CE*/ES*/GO*/MA*/MS*/PA*/PB*/PI*/RN*/RR*/SC*/SE*/TO*';
		var dig10 = '/PR*/RS*';
		var dig11 = '/MT*';
		var dig12 = '/SP*';
		var dig13 = '/AC*/MG*/DF*';
		var dig14 = '/PE*/RO*';

		if (dig8.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 8);
		} else if (dig9.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 9);
		} else if (dig10.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 10);
		} else if (dig11.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 11);
		} else if (dig12.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 12);
		} else if (dig13.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 13);
		} else if (dig14.indexOf('/' + estado + '*') != -1) {
			ie = ie.substr(0, 14);
		} else {
			ie = '';
		}
	}
	if (ie != '') {
		if (estado === 'PR') {
			var primDigito = 0;
			var seguDigito = 0;
			var pesos = '32765432';
			var soma = 0;
			for (var i = 0; i < pesos.length; i++) {
				soma = soma + parseInt(ie.substr(i, 1)) * parseInt(pesos.substr(i, 1));
			}
			primDigito = 11 - (soma % 11);
			if (primDigito > 9) primDigito = 0;
			pesos = '432765432';
			soma = 0;
			for (i = 0; i < pesos.length; i++) {
				soma = soma + parseInt(ie.substr(i, 1)) * parseInt(pesos.substr(i, 1));
			}
			seguDigito = 11 - (soma % 11);
			if (seguDigito > 9) seguDigito = 0;

			return parseInt(ie.substr(8, 1)) === primDigito && parseInt(ie.substr(9, 1)) === seguDigito;
		}
	}
};

// By @Salles_FA
const validarSuframa = value => {
	value = value.replace(/\D/g, '');
	if (isNaN(value) || value.length !== 9) {
		return false;
	}
	const cod = value.split('');

	const dig = cod.pop();
	const dv = 11 - (cod.reduce((a, b, i) => a + (9 - i) * parseInt(b), 0) % 11);

	// eslint-disable-next-line eqeqeq
	return dig == (dv > 9 ? 0 : dv);
};

export {
	formataCEP,
	formataCPFCNPJ,
	formataCurrency,
	formataData,
	formataDataHora,
	formataTelefone,
	getRandomID,
	getVal,
	isValNumber,
	roundFloat,
	setAllNull,
	setAllTrue,
	validarCNPJ,
	validarCPF,
	validarIE,
	validarSuframa,
};
